import { create } from 'zustand';
import {
  sendCommand as sendCommandDirect,
  type ExtensionPresence,
  type SessionState,
  type SyncCommandInput
} from './client';
import { startTransport, type RealtimeTransport } from './transport';

// Single source of truth for the RTDB control-plane state on the web-app/embed
// side (see refs/realtime-sync). Every consumer shares ONE presence + sessions
// subscription via the ref-counted `connect()` action, instead of each hook
// opening its own `onValue` listener.
//
// Across realms (multiple embed iframes / tabs) the shared subscription is held
// by a single leader-elected frame via `leaderTransport`; followers get state
// over a BroadcastChannel. So the whole browser keeps ONE RTDB socket, not one
// per slide. `connect()` here is the per-realm ref-count over that transport.

const EMPTY_PRESENCE: ExtensionPresence = { installed: false, online: false, pages: {} };

export interface RealtimeSyncStore {
  presence: ExtensionPresence;
  sessions: Record<string, SessionState>;

  // --- Phase 2 command dispatch ---
  // Ask the extension to start/stop reading chat for a livestream. Resolves once
  // the command is written to RTDB; the resulting state arrives via `sessions`.
  startReading: (
    livestreamId: string,
    opts?: Omit<SyncCommandInput, 'type' | 'livestreamId'>
  ) => Promise<void>;
  stopReading: (livestreamId: string) => Promise<void>;

  // Tell the leader whose app presence to publish (the current user/owner).
  // Safe to call before connect(); applied once the transport is running.
  setPresenceIdentity: (userId: string | null) => void;

  // Announce the current team owner key (from the teams store). A switch between
  // two known owners restarts the running transport so subscriptions, presence
  // and commands re-auth under the new `/sync/{ownerId}` instead of silently
  // staying on the previous owner's. Safe to call before connect().
  setOwnerKey: (ownerKey: string | null) => void;

  // --- Internal subscription manager (do not use from components) ---
  _refCount: number;
  _transport: RealtimeTransport | null;
  _presenceUserId: string | null;
  _ownerKey: string | null;
  // Open the shared subscription (idempotent, ref-counted). Returns a release
  // function; the last release tears the subscription down and resets state.
  connect: () => () => void;
}

export const useRealtimeSyncStore = create<RealtimeSyncStore>()((set, get) => {
  // Start a transport for the current owner key and register it on the store.
  const startStoreTransport = () => {
    const transport = startTransport(
      {
        onPresence: (presence) => set({ presence }),
        onSessions: (sessions) => set({ sessions })
      },
      get()._ownerKey
    );
    // Re-apply any identity registered before the transport started.
    transport.setPresenceIdentity(get()._presenceUserId);
    set({ _transport: transport });
  };

  return {
    presence: EMPTY_PRESENCE,
    sessions: {},

    startReading: async (livestreamId, opts = {}) => {
      const command = { type: 'startReading' as const, livestreamId, ...opts };
      const transport = get()._transport;
      // When connected, route through the leader's single socket; otherwise (no
      // active consumer) just write directly — one-off fire-and-forget.
      await (transport ? transport.sendCommand(command) : sendCommandDirect(command));
    },
    stopReading: async (livestreamId) => {
      const command = { type: 'stopReading' as const, livestreamId };
      const transport = get()._transport;
      await (transport ? transport.sendCommand(command) : sendCommandDirect(command));
    },

    setPresenceIdentity: (userId) => {
      set({ _presenceUserId: userId });
      get()._transport?.setPresenceIdentity(userId);
    },

    setOwnerKey: (ownerKey) => {
      const prev = get()._ownerKey;
      const next = ownerKey ? String(ownerKey) : null;
      if (prev === next) return;
      set({ _ownerKey: next });
      // Restart only on a switch between two *known* owners. The initial
      // null -> owner resolution doesn't invalidate anything (the first sign-in
      // fetches its token with the current auth headers anyway), and restarting
      // there would churn the connection on every page load.
      if (prev && next && get()._transport) {
        get()._transport?.stop();
        set({ presence: EMPTY_PRESENCE, sessions: {} });
        startStoreTransport();
      }
    },

    _refCount: 0,
    _transport: null,
    _presenceUserId: null,
    _ownerKey: null,
    connect: () => {
      const nextCount = get()._refCount + 1;
      set({ _refCount: nextCount });

      if (nextCount === 1) startStoreTransport();

      return () => {
        const remaining = get()._refCount - 1;
        if (remaining > 0) {
          set({ _refCount: remaining });
          return;
        }
        get()._transport?.stop();
        set({
          _refCount: 0,
          _transport: null,
          presence: EMPTY_PRESENCE,
          sessions: {}
        });
      };
    }
  };
});
